import { useState } from "react";
import clsx from "clsx";
import { DateTime } from "luxon";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BookOpen, Check, Copy, KeyRound, Loader2, MessageSquare, Plus, RefreshCw, Send, Trash2, Webhook } from "lucide-react";
import type { AirportConfig } from "@yb/core";
import { api, ApiError, type DataSources, type DayAhead, type IntegrationsInfo } from "../lib/api";
import { stamp } from "../lib/format";
import { needsAttention, shortDay, sourceMix } from "../lib/days-ahead";
import { UploadLoads } from "./UploadLoads";

const input = "rounded-md border border-line bg-panel px-2 py-1 text-[12.5px] disabled:opacity-70";
const card = "rounded-xl border border-line p-4";
const btn = "flex items-center gap-1 rounded-lg border border-line px-3 py-1.5 text-[12.5px] font-semibold hover:border-ink-3 disabled:opacity-50";

/** How long ago, or once it is more than an hour, the time in the airport's zone (the browser's where no airport is to hand). */
export function ago(ms: number | null | undefined, tz?: string): string {
  if (!ms) return "never";
  const m = Math.round((Date.now() - ms) / 60_000);
  if (m < 1) return "just now";
  if (m < 60) return `${m} min ago`;
  return tz ? stamp(ms, tz) : DateTime.fromMillis(ms).toFormat("d LLL HH:mm");
}

export function CopyText({ text }: { text: string }) {
  const [done, setDone] = useState(false);
  return (
    <span className="inline-flex max-w-full items-center gap-1 rounded-md bg-panel-2 px-2 py-1 font-mono text-[11.5px]">
      <span className="truncate">{text}</span>
      <button
        className="shrink-0 text-ink-3 hover:text-ink"
        onClick={() => {
          void navigator.clipboard?.writeText(text);
          setDone(true);
          setTimeout(() => setDone(false), 1500);
        }}
        aria-label="Copy"
      >
        {done ? <Check className="size-3.5 text-ok" /> : <Copy className="size-3.5" />}
      </button>
    </span>
  );
}

function Status({ tone, children }: { tone: "ok" | "warn" | "bad" | "off"; children: React.ReactNode }) {
  return (
    <span
      className={clsx(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold",
        tone === "ok" && "bg-ok/15 text-ok",
        tone === "warn" && "bg-warn/15 text-warn",
        tone === "bad" && "bg-bad/15 text-bad",
        tone === "off" && "bg-panel-2 text-ink-3",
      )}
    >
      <span className="size-1.5 rounded-full bg-current" />
      {children}
    </span>
  );
}

/**
 * What each scope is for. Every scope the server offers is listed by its own name: a
 * label written as a two-way choice silently mislabels the third one, which is how
 * bags:write came to read as "apron:write" on screen.
 */
export const SCOPE_WHO: Record<string, string> = {
  read: "Display, Helix",
  "apron:write": "Vectro Apron",
  "bags:write": "VectroLake",
};
export const SCOPE_HELP: Record<string, string> = {
  read: "Read flights, resources, changes and the summary. No writes.",
  "apron:write": "Send observed apron events: on/off-block times, gate status, alerts.",
  "bags:write": "Send bag counts and bag events. Cannot read the plan.",
};

// ---------------------------------------------------------------- data sources

export function DataSourcesSection({
  draft,
  set,
  canEdit,
}: {
  draft: AirportConfig;
  set: (fn: (c: AirportConfig) => void) => void;
  canEdit: boolean;
}) {
  const q = useQuery({ queryKey: ["data-sources"], queryFn: api.dataSources, refetchInterval: 20_000 });
  const ahead = useQuery({ queryKey: ["days-ahead"], queryFn: api.daysAhead, refetchInterval: 60_000 });
  const qc = useQueryClient();
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const src = draft.loadSources ?? { sheets: [], pollMinutes: 10 };
  const d = q.data;
  const tz = draft.timezone;

  return (
    <>
      <h2 className="mb-1 text-[18px] font-bold">Data sources</h2>
      <p className="mb-4 text-[12.5px] text-ink-3">
        Passenger and bag loads, and security screening. The newest number for a flight wins; each flight shows where its load came from.
      </p>

      <div className={clsx(card, "mb-3")}>
        <div className="mb-1 flex items-center gap-2 text-[14px] font-bold">Google Sheets</div>
        <p className="mb-3 text-[12px] text-ink-3">
          Share each sheet as “anyone with the link can view” (or File → Share → Publish to web) and paste the link. Vectro Allocate reads the first tab (or the tab in the link) every few minutes and finds the flight, date, pax and bag columns itself.
        </p>
        <div className="space-y-2">
          {src.sheets.map((sh, i) => (
            <div key={sh.id} className="flex flex-wrap items-center gap-2">
              <input
                type="checkbox"
                className="size-4"
                checked={sh.enabled}
                disabled={!canEdit}
                onChange={(e) => set((c) => (c.loadSources!.sheets[i]!.enabled = e.target.checked))}
              />
              <input className={clsx(input, "w-44")} value={sh.name} disabled={!canEdit} placeholder="Name" onChange={(e) => set((c) => (c.loadSources!.sheets[i]!.name = e.target.value))} />
              <input className={clsx(input, "min-w-72 flex-1")} value={sh.url} disabled={!canEdit} placeholder="https://docs.google.com/spreadsheets/d/…" onChange={(e) => set((c) => (c.loadSources!.sheets[i]!.url = e.target.value))} />
              <select
                className={clsx(input, "w-40")}
                value={sh.airline ?? ""}
                disabled={!canEdit}
                title="Airline for flight numbers written without one (e.g. 321 → PD321)"
                onChange={(e) => set((c) => (c.loadSources!.sheets[i]!.airline = e.target.value || null))}
              >
                <option value="">Airline in sheet</option>
                {Object.values(draft.airlines).map((a) => (
                  <option key={a.code} value={a.code}>
                    Numbers are {a.iata}
                  </option>
                ))}
              </select>
              {canEdit && (
                <button className="rounded p-1 text-ink-3 hover:text-bad" onClick={() => set((c) => c.loadSources!.sheets.splice(i, 1))} aria-label="Remove">
                  <Trash2 className="size-4" />
                </button>
              )}
            </div>
          ))}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {canEdit && (
            <button
              className={btn}
              onClick={() =>
                set((c) => {
                  c.loadSources = c.loadSources ?? { sheets: [], pollMinutes: 10 };
                  c.loadSources.sheets.push({ id: `sh-${Date.now().toString(36)}`, name: "Airline loads", url: "", enabled: true });
                })
              }
            >
              <Plus className="size-4" /> Add sheet
            </button>
          )}
          <span className="text-[12px] text-ink-3">Check every</span>
          <input
            type="number"
            className={clsx(input, "w-16")}
            value={src.pollMinutes}
            disabled={!canEdit}
            onChange={(e) =>
              set((c) => {
                c.loadSources = c.loadSources ?? { sheets: [], pollMinutes: 10 };
                c.loadSources.pollMinutes = Math.max(1, Number(e.target.value) || 10);
              })
            }
          />
          <span className="text-[12px] text-ink-3">minutes</span>
          <button
            className={btn}
            disabled={busy !== null}
            onClick={async () => {
              setBusy("poll");
              try {
                await api.pollSheets();
                await qc.invalidateQueries();
              } finally {
                setBusy(null);
              }
            }}
          >
            {busy === "poll" ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />} Read saved sheets now
          </button>
        </div>
      </div>

      <div className={clsx(card, "mb-3")}>
        <div className="mb-1 flex items-center gap-2 text-[14px] font-bold">
          Forwarded emails
          {d && <Status tone={d.inboundEmail.enabled || d.imap.configured ? "ok" : "off"}>{d.inboundEmail.enabled || d.imap.configured ? "On" : "Not set up"}</Status>}
        </div>
        <p className="mb-2 text-[12px] text-ink-3">
          Forward load emails and Vectro Allocate reads Excel/CSV attachments, tables in the email, Google Sheets links, and lines like “WS181 18SEP PAX 150 BAGS 120”. A date in the subject (“Loads 18SEP”) applies to rows without one.
        </p>
        {d?.inboundEmail.path && (
          <div className="mb-2 text-[12px]">
            Webhook for an inbound-email service (e.g. Postmark): <CopyText text={`${location.origin}${d.inboundEmail.path}`} />
          </div>
        )}
        {d?.imap.configured && (
          <div className="mb-2 text-[12px] text-ink-2">
            Mailbox {d.imap.mailbox ?? ""}: checked {ago(d.imap.lastRun, tz)}, {d.imap.processed} emails read
            {d.imap.lastError && <span className="text-bad"> · {d.imap.lastError}</span>}
            <div className="text-ink-3">
              {d.imap.onlyTo
                ? `Only mail addressed to ${d.imap.onlyTo} is read; everything else in that mailbox is left alone.`
                : "Every unread message in that mailbox is read. Set LOADS_EMAIL to the loads alias so only mail addressed to it is touched."}
            </div>
          </div>
        )}
        {d && !d.inboundEmail.enabled && !d.imap.configured && (
          <div className="rounded-lg bg-panel-2 p-2.5 text-[12px] text-ink-2">
            To switch this on, add Secrets in Replit: either <b>IMAP_HOST</b>, <b>IMAP_USER</b>, <b>IMAP_PASSWORD</b> for a mailbox the emails are forwarded to (e.g. a Gmail account with an app password), or <b>INBOUND_EMAIL_TOKEN</b> for an inbound-email webhook.
          </div>
        )}
      </div>

      <div className={clsx(card, "mb-3")}>
        <div className="mb-1 text-[14px] font-bold">Upload a file</div>
        <p className="mb-2 text-[12px] text-ink-3">Excel (.xlsx) or CSV with flight numbers and pax and/or bags. The same button is on the Forecast and Bags pages.</p>
        <UploadLoads />
      </div>

      <Bpss2Card bpss2={d?.bpss2} tz={tz} />

      <div className={clsx(card, "mb-3")}>
        <div className="mb-1 flex items-center gap-2 text-[14px] font-bold">
          CopOpt (Better Airport)
          {d?.copopt && (
            <Status tone={d.copopt.state === "ok" ? "ok" : d.copopt.configured ? "bad" : "off"}>
              {d.copopt.state === "ok" ? "Connected" : d.copopt.configured ? "Needs attention" : "Not set up"}
            </Status>
          )}
        </div>
        <p className="text-[12px] text-ink-3">Read-only: Vectro Allocate only reads flights, aircraft, seats and loads from CopOpt and never sends anything back.</p>
        {d?.copopt && (
          <div className="mt-1 text-[12px] text-ink-2">
            {d.copopt.message}
            {d.copopt.lastCheck ? ` · checked ${ago(d.copopt.lastCheck, tz)}` : ""}
          </div>
        )}
        {d?.copoptSchedule && d.copoptSchedule.state !== "off" && (
          <div className={clsx("mt-1 text-[12px]", d.copoptSchedule.state === "ok" ? "text-ink-2" : "text-bad")}>
            Schedule: {d.copoptSchedule.message}
            {d.copoptSchedule.at ? ` · ${ago(d.copoptSchedule.at, tz)}` : ""}
          </div>
        )}
        {d?.copopt && d.copopt.state !== "ok" && (
          <div className="mt-1 text-[12px] text-ink-3">
            While CopOpt is not connected, days beyond the feed are projected from the last four same weekdays instead of read from its schedule.
          </div>
        )}
        {canEdit && <CopOptLogin onDone={() => qc.invalidateQueries({ queryKey: ["data-sources"] })} />}
      </div>

      {ahead.data && <DaysAheadCard days={ahead.data.days} tz={tz} />}

      {d?.security && (
        <div className={clsx(card, "mb-3")}>
          <div className="mb-1 flex items-center gap-2 text-[14px] font-bold">
            Security checkpoint
            <Status tone={d.security.capacity.basis === "assumed" ? "warn" : "ok"}>{d.security.capacity.basis === "set" ? "Set" : "Assumed"}</Status>
          </div>
          <p className="text-[12px] text-ink-3">
            {d.security.capacity.lanes} lanes. We record how many passengers clear screening each quarter hour, counted from boarding-pass scans, which gives
            the real peaks here. BPSS2&apos;s lanes-open figure is deliberately not used: it does not reflect what is actually open.
          </p>
          <div className="mt-1 text-[12px] text-ink-2">
            Planning to <span className="font-semibold">{d.security.capacity.perLaneHour} passengers per lane per hour</span> ·{" "}
            {d.security.capacity.checkpointPerHour.toLocaleString()}/hour across {d.security.capacity.lanes} lanes
            {d.security.capacity.observedPeakPerHour ? ` · busiest recorded ${d.security.capacity.observedPeakPerHour.toLocaleString()}/hour` : ""}
          </div>
          <div className="mt-1 text-[12px] text-ink-3">{d.security.capacity.note}</div>
          <div className="mt-1 text-[12px] text-ink-3">The lanes, the figure and the target wait are set under General &amp; timings.</div>
          <div className="mt-1 text-[12px] text-ink-2">
            {d.security.message}
            {d.security.at ? ` · ${ago(d.security.at, tz)}` : ""}
          </div>
        </div>
      )}

      {d?.bags && (
        <div className={clsx(card, "mb-3")}>
          <div className="mb-1 flex items-center gap-2 text-[14px] font-bold">
            Baggage data collection
            <Status tone={d.bags.state === "ok" ? "ok" : d.bags.state === "quiet" ? "warn" : "off"}>
              {d.bags.state === "ok" ? "Receiving" : d.bags.state === "quiet" ? "Connected, quiet" : "Not reporting"}
            </Status>
          </div>
          <p className="text-[12px] text-ink-3">
            The bag system on the local server sends one event per bag as it is checked in, screened, made up and loaded. Bag tags are hashed on arrival
            and never stored. Counts appear on the board beside the passenger figures.
          </p>
          <div className="mt-1 text-[12px] text-ink-2">
            {d.bags.message}
            {d.bags.at ? ` · last event ${ago(d.bags.at, tz)}` : ""}
          </div>
          <div className="mt-1 text-[12px] text-ink-3">
            It needs an API key with the <span className="font-semibold">bags:write</span> scope, made below under Integrations &amp; API. The contract is
            at <span className="font-semibold">/api/docs</span>.
          </div>
        </div>
      )}

      {d?.carryOver && d.carryOver.mode !== "off" && (
        <div className={clsx(card, "mb-3")}>
          <div className="mb-1 flex items-center gap-2 text-[14px] font-bold">
            {d.carryOver.mode === "export" ? "Carry-over to the published app" : "Carried over from the workspace"}
            <Status tone={d.carryOver.state === "ok" ? "ok" : d.carryOver.state === "error" ? "bad" : "off"}>
              {d.carryOver.state === "ok" ? "Working" : d.carryOver.state === "error" ? "Needs attention" : "Waiting"}
            </Status>
          </div>
          <p className="text-[12px] text-ink-3">
            {d.carryOver.mode === "export"
              ? "Users, settings, API keys, webhooks, SMS contacts, logins, closures and tows/mech holds made here are added to the published app every time it starts. Nothing changed in the published app is overwritten."
              : "At every start, users and setup made in the Replit workspace are added here. Nothing changed here is overwritten."}
          </p>
          <div className="mt-1 text-[12px] text-ink-2">
            {d.carryOver.message}
            {d.carryOver.at ? ` · ${ago(d.carryOver.at, tz)}` : ""}
          </div>
          {d.carryOver.mode === "import" && d.carryOver.database === "embedded" && (
            <div className="mt-1 text-[12px] font-semibold text-bad">
              This published app has no Postgres database, so its data is lost on every publish. Add a production database in Replit.
            </div>
          )}
          {d.carryOver.mode === "import" && d.carryOver.pendingSettings && (
            <div className="mt-2 rounded-lg border border-info/40 bg-info/5 p-2.5 text-[12px]">
              <div className="font-semibold">A newer settings version is waiting from the workspace</div>
              <div className="mt-0.5 text-ink-2">
                &ldquo;{d.carryOver.pendingSettings.note}&rdquo; &middot; {ago(new Date(d.carryOver.pendingSettings.createdAt).getTime(), tz)}
              </div>
              <div className="mt-1 text-ink-3">
                Nothing here changes until an administrator applies it. Applying saves it as a new settings version on this app and re-plans the day, like any save on the
                Settings screen; what the airport saved before stays in the settings history.
              </div>
              {canEdit && (
                <button
                  className={clsx(btn, "mt-2")}
                  disabled={busy === "apply-settings"}
                  onClick={async () => {
                    setBusy("apply-settings");
                    setMsg(null);
                    try {
                      const r = await api.applyWorkspaceSettings();
                      setMsg(`Applied: ${r.note}`);
                      await qc.invalidateQueries();
                    } catch (err) {
                      setMsg(err instanceof Error ? err.message : "Could not apply");
                    } finally {
                      setBusy(null);
                    }
                  }}
                >
                  {busy === "apply-settings" ? <Loader2 className="size-4 animate-spin" /> : null} Apply here
                </button>
              )}
            </div>
          )}
        </div>
      )}

      <div className={card}>
        <div className="mb-2 text-[14px] font-bold">Recent imports</div>
        {!d?.log.length && <div className="text-[12px] text-ink-3">Nothing imported since the server started.</div>}
        <div className="divide-y divide-line">
          {d?.log.map((l, i) => (
            <div key={i} className="flex items-baseline gap-3 py-1.5 text-[12px]">
              <span className="tabular w-24 shrink-0 text-ink-3">{ago(l.at, tz)}</span>
              <span className="w-44 shrink-0 truncate font-semibold" title={l.source}>
                {l.source}
              </span>
              <span className="min-w-0 flex-1 truncate text-ink-2" title={`${l.ref} ${l.note}`}>
                {l.ref} · {l.note}
              </span>
              <span className={clsx("tabular shrink-0", l.records && l.matched === 0 ? "text-warn" : "text-ink-2")}>
                {l.records} read · {l.matched} matched
              </span>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

/**
 * The days ahead, one row each: how many flights, where they came from, and what has no
 * stand. This is where "a flight is missing from Friday" is answered: a thin day or a
 * day resting on a projection alone says which source is not there.
 */
/**
 * The BPSS2 card. A read that was cut short with nothing in memory to make up the loss
 * is shown as Partial read in warn tone with the poller's own words, because the counts
 * for the first flights of the day may be short and anyone reading the board should
 * know that before believing a low figure. Hitting the row limit on its own is not
 * that: every busy weekday afternoon does, while earlier reads keep the counts whole,
 * and amber every afternoon would be amber nobody looks at. Then the card stays
 * Connected and the message says the limit was hit.
 */
export function Bpss2Card({ bpss2, tz }: { bpss2: DataSources["bpss2"] | undefined; tz: string }) {
  const b = bpss2;
  const tone = !b ? null : b.state === "ok" ? (b.short ? "warn" : "ok") : b.mode === "off" ? "off" : b.state === "error" ? "bad" : "warn";
  const label = !b ? null : b.state === "ok" ? (b.short ? "Partial read" : "Connected") : b.mode === "off" ? "Not set up" : b.state === "error" ? "Needs attention" : "Starting";
  return (
    <div className={clsx(card, "mb-3")} data-bpss2={tone ?? undefined}>
      <div className="mb-1 flex items-center gap-2 text-[14px] font-bold">
        BPSS2 security screening
        {tone && label && <Status tone={tone}>{label}</Status>}
      </div>
      {b && (
        <div className={clsx("text-[12px]", b.short ? "text-warn" : "text-ink-2")}>
          {b.message}
          {b.lastSuccess ? ` · updated ${ago(b.lastSuccess, tz)}` : ""}
        </div>
      )}
      <div className="mt-1 text-[12px] text-ink-3">
        Scans also build each departure&apos;s show-up curve and the checkpoint&apos;s load per quarter hour. Counts only grow during a day, and a flight whose scans a
        cut-short read could have missed is marked partial and not learned from. BPSS2&apos;s lanes-open figure is not read.
      </div>
    </div>
  );
}

export function DaysAheadCard({ days, tz }: { days: DayAhead[]; tz: string }) {
  return (
    <div className={clsx(card, "mb-3")}>
      <div className="mb-1 text-[14px] font-bold">Days ahead</div>
      <p className="mb-2 text-[12px] text-ink-3">
        Each day beyond the feed is built from the feed&apos;s own flights, then CopOpt&apos;s schedule when it is connected, then the last four same weekdays. The plan-ahead job
        gives every day a plan each hour; a day with aircraft on no stand, or with very few flights, is marked.
      </p>
      <table className="w-full text-[12.5px]">
        <thead>
          <tr className="border-b border-line text-left text-[11px] tracking-wide text-ink-3 uppercase">
            <th className="px-2 py-1.5">Day</th>
            <th className="px-2 py-1.5 text-right">Flights</th>
            <th className="px-2 py-1.5">From</th>
            <th className="px-2 py-1.5 text-right">No stand</th>
            <th className="px-2 py-1.5" />
          </tr>
        </thead>
        <tbody>
          {days.map((d) => (
            <tr key={d.date} className="border-b border-line" data-day-ahead={needsAttention(d) ? "warn" : "ok"}>
              <td className="px-2 py-1.5 font-semibold">{shortDay(d.date, tz)}</td>
              <td className="tabular px-2 py-1.5 text-right">
                {d.flights} <span className="text-ink-3">({d.arrivals} arr, {d.departures} dep)</span>
              </td>
              <td className="px-2 py-1.5 text-ink-2">{sourceMix(d, tz)}</td>
              <td className={clsx("tabular px-2 py-1.5 text-right", d.unallocated > 0 && "font-semibold text-warn")}>{d.unallocated}</td>
              <td className="px-2 py-1.5">
                <Status tone={needsAttention(d) ? "warn" : d.planned ? "ok" : "off"}>{needsAttention(d) ? "Needs attention" : d.planned ? "Planned" : "Not planned yet"}</Status>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CopOptLogin({ onDone }: { onDone: () => void }) {
  const [user, setUser] = useState("kelowna");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; status: number; message: string } | null>(null);
  return (
    <form
      className="mt-3 flex flex-wrap items-center gap-2 rounded-lg bg-panel-2 p-2.5"
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        setResult(null);
        try {
          const r = await api.copoptLogin(user, password);
          setResult(r);
          if (r.ok) {
            setPassword("");
            onDone();
          }
        } catch (err) {
          setResult({ ok: false, status: 0, message: err instanceof ApiError ? err.message : "Test failed" });
        } finally {
          setBusy(false);
        }
      }}
    >
      <span className="text-[12px] font-semibold">Test and save login</span>
      <input className={clsx(input, "w-36")} value={user} onChange={(e) => setUser(e.target.value)} placeholder="API username" autoComplete="off" />
      <input className={clsx(input, "w-56")} type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="API password" autoComplete="new-password" />
      <button className={btn} disabled={busy || !user || !password}>
        {busy ? <Loader2 className="size-4 animate-spin" /> : null} Test
      </button>
      {result && (
        <span className={clsx("text-[12px] font-semibold", result.ok ? "text-ok" : "text-bad")}>
          {result.ok ? `${result.status} OK: saved. ${result.message}` : `${result.status || ""} ${result.message}`}
        </span>
      )}
    </form>
  );
}

// ---------------------------------------------------------------- texting

export function TextingSection({ canEdit, tz }: { canEdit: boolean; tz: string }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["notifications"], queryFn: api.notifications, refetchInterval: 10_000 });
  const [contacts, setContacts] = useState<Array<{ name: string; phone: string; canApprove: boolean; active: boolean }> | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const d = q.data;
  const list = contacts ?? d?.contacts.map(({ name, phone, canApprove, active }) => ({ name, phone, canApprove, active })) ?? [];
  const approver = list.find((c) => c.active && c.canApprove);

  return (
    <>
      <h2 className="mb-1 text-[18px] font-bold">Texting & auto-fix</h2>
      <p className="mb-4 text-[12.5px] text-ink-3">
        When a stand conflict appears, Vectro Allocate fixes it on its own if it is far enough away (set the window under General & timings). Closer conflicts are texted to approvers with numbered options; replying with a number applies that option.
      </p>

      <div className={clsx(card, "mb-3")}>
        <div className="mb-1 flex items-center gap-2 text-[14px] font-bold">
          Text messages
          {d && <Status tone={d.live ? "ok" : "warn"}>{d.live ? "Live (Twilio)" : "Simulated"}</Status>}
        </div>
        {d && !d.live && (
          <div className="rounded-lg bg-panel-2 p-2.5 text-[12px] text-ink-2">
            Messages are shown below instead of being sent. To send real texts, add Secrets in Replit: <b>TWILIO_ACCOUNT_SID</b>, <b>TWILIO_AUTH_TOKEN</b>, <b>TWILIO_TWOWAY_NUMBER</b>, then set the number's “A message comes in” webhook to <CopyText text={d.replyUrl} />
          </div>
        )}
        {d?.live && (
          <div className="text-[12px] text-ink-2">
            Replies arrive at <CopyText text={d.replyUrl} />
          </div>
        )}
        {d?.replyRoute?.state === "wrong" && (
          <div className="mt-2 rounded-lg border border-bad/40 bg-bad/10 p-2.5 text-[12px] text-bad">
            Replies are not reaching the app. In Twilio, the sending number&apos;s &ldquo;A message comes in&rdquo; webhook is{" "}
            {d.replyRoute.url ? <b>{d.replyRoute.url}</b> : <b>empty</b>}; set it to <CopyText text={d.replyUrl} /> (HTTP POST). Until then a reply to a conflict text is
            received by Twilio and goes nowhere.
          </div>
        )}
        {d?.replyRoute?.state === "ok" && <div className="mt-1 text-[12px] text-ok">Twilio sends replies here: checked.</div>}
        {d?.replyRoute?.state === "unknown" && (
          <div className="mt-1 text-[12px] text-warn">{`Could not check where Twilio sends replies${d.replyRoute.note ? `: ${d.replyRoute.note}` : ""}.`}</div>
        )}
        {d?.resolver.lastAction && <div className="mt-2 text-[12px] text-ink-2">Last action: {d.resolver.lastAction}</div>}
        <button
          className={clsx(btn, "mt-2")}
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            try {
              await api.checkNow();
              await qc.invalidateQueries();
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />} Check for conflicts now
        </button>
      </div>

      <TowCrews canEdit={canEdit} />

      <div className={clsx(card, "mb-3")}>
        <div className="mb-2 text-[14px] font-bold">Who gets texts</div>
        <div className="space-y-2">
          {list.map((c, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2">
              <input className={clsx(input, "w-44")} value={c.name} disabled={!canEdit} placeholder="Name" onChange={(e) => setContacts(list.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} />
              <input className={clsx(input, "w-44")} value={c.phone} disabled={!canEdit} placeholder="250-555-0123" onChange={(e) => setContacts(list.map((x, j) => (j === i ? { ...x, phone: e.target.value } : x)))} />
              <label className="flex items-center gap-1 text-[12px]">
                <input type="checkbox" checked={c.canApprove} disabled={!canEdit} onChange={(e) => setContacts(list.map((x, j) => (j === i ? { ...x, canApprove: e.target.checked } : x)))} /> Can approve
              </label>
              <label className="flex items-center gap-1 text-[12px]">
                <input type="checkbox" checked={c.active} disabled={!canEdit} onChange={(e) => setContacts(list.map((x, j) => (j === i ? { ...x, active: e.target.checked } : x)))} /> On duty
              </label>
              {canEdit && (
                <button className="rounded p-1 text-ink-3 hover:text-bad" onClick={() => setContacts(list.filter((_, j) => j !== i))} aria-label="Remove">
                  <Trash2 className="size-4" />
                </button>
              )}
            </div>
          ))}
        </div>
        {canEdit && (
          <div className="mt-3 flex items-center gap-2">
            <button className={btn} onClick={() => setContacts([...list, { name: "", phone: "", canApprove: true, active: true }])}>
              <Plus className="size-4" /> Add person
            </button>
            {contacts && (
              <button
                className="rounded-lg bg-brand px-3 py-1.5 text-[12.5px] font-bold text-brand-ink"
                onClick={async () => {
                  try {
                    await api.saveContacts(contacts);
                    setContacts(null);
                    setMsg("Saved");
                    await qc.invalidateQueries({ queryKey: ["notifications"] });
                  } catch (err) {
                    setMsg(err instanceof ApiError ? err.message : "Could not save");
                  }
                }}
              >
                Save people
              </button>
            )}
            {msg && <span className="text-[12px] text-ink-2">{msg}</span>}
          </div>
        )}
      </div>

      <div className={clsx(card, "mb-3")}>
        <div className="mb-2 text-[14px] font-bold">Waiting for an answer</div>
        {d?.approvals.filter((a) => a.status === "pending").length === 0 && <div className="text-[12px] text-ink-3">Nothing waiting.</div>}
        {d?.approvals
          .filter((a) => a.status === "pending")
          .map((a) => (
            <div key={a.id} className="mb-2 rounded-lg bg-warn/5 p-2.5">
              <pre className="font-sans text-[12px] whitespace-pre-wrap">{a.message}</pre>
              {!d.live && approver && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {[...a.options.map((o) => String(o.n)), "0"].map((n) => (
                    <button
                      key={n}
                      className={btn}
                      onClick={async () => {
                        const r = await api.simulateReply(approver.phone.startsWith("+") ? approver.phone : `+1${approver.phone.replace(/\D/g, "").slice(-10)}`, n);
                        setReply(`${approver.name} replied ${n}: ${r.answer}`);
                        await qc.invalidateQueries();
                      }}
                    >
                      Reply {n}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        {reply && <div className="text-[12px] text-ok">{reply}</div>}
        {d?.approvals.filter((a) => a.status !== "pending").length ? (
          <div className="mt-3 divide-y divide-line">
            {d.approvals
              .filter((a) => a.status !== "pending")
              .slice(0, 8)
              .map((a) => (
                <div key={a.id} className="flex gap-3 py-1.5 text-[12px]">
                  <span className="w-20 shrink-0 font-semibold capitalize">{a.status}</span>
                  <span className="min-w-0 flex-1 truncate text-ink-2" title={a.message}>
                    {a.result ?? a.message.split("\n")[0]}
                  </span>
                  <span className="shrink-0 text-ink-3">{a.resolvedBy ?? ""}</span>
                </div>
              ))}
          </div>
        ) : null}
      </div>

      <div className={card}>
        <div className="mb-2 flex items-center gap-2 text-[14px] font-bold">
          <MessageSquare className="size-4" /> Message log
        </div>
        <div className="divide-y divide-line">
          {d?.log.map((m) => (
            <div key={m.id} className="flex gap-3 py-1.5 text-[12px]">
              <span className="tabular w-28 shrink-0 text-ink-3">{stamp(m.at, tz)}</span>
              <span className={clsx("w-10 shrink-0 font-semibold", m.direction === "in" ? "text-info" : "text-ink-2")}>{m.direction === "in" ? "From" : "To"}</span>
              <span className="w-32 shrink-0 truncate">{m.name ?? m.phone}</span>
              <span className="min-w-0 flex-1 whitespace-pre-wrap text-ink-2">{m.body}</span>
              <span className="shrink-0 text-ink-3">{m.status}</span>
            </div>
          ))}
          {!d?.log.length && <div className="text-[12px] text-ink-3">No messages yet.</div>}
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------- API & integrations

export function ApiSection({ canEdit, tz }: { canEdit: boolean; tz: string }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["integrations"], queryFn: api.integrations, enabled: canEdit });
  const [keyName, setKeyName] = useState("Display");
  const [scopes, setScopes] = useState<string[]>(["read"]);
  const [newSecret, setNewSecret] = useState<{ label: string; value: string } | null>(null);
  const [hookName, setHookName] = useState("Display");
  const [hookUrl, setHookUrl] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const d = q.data;
  const refresh = () => qc.invalidateQueries({ queryKey: ["integrations"] });

  if (!canEdit) {
    return (
      <>
        <h2 className="mb-1 text-[18px] font-bold">Integrations & API</h2>
        <p className="text-[12.5px] text-ink-3">Only administrators can manage API keys and webhooks.</p>
        <a href="/api/docs" target="_blank" rel="noreferrer" className={clsx(btn, "mt-3 inline-flex")}>
          <BookOpen className="size-4" /> Open the API documentation
        </a>
      </>
    );
  }

  return (
    <>
      <h2 className="mb-1 text-[18px] font-bold">Integrations & API</h2>
      <p className="mb-4 text-[12.5px] text-ink-3">
        Vectro Apron sends apron events in; Display and Helix read allocations out. Every connection uses its own API key, so it can be switched off on its own.
      </p>

      <div className={clsx(card, "mb-3 flex flex-wrap items-center gap-3")}>
        <BookOpen className="size-5 text-info" />
        <div className="min-w-0 flex-1">
          <div className="text-[14px] font-bold">API documentation (v{d?.apiVersion ?? "1"})</div>
          <div className="text-[12px] text-ink-3">Interactive reference for every endpoint, the webhook payload and the Apron contract.</div>
        </div>
        <a href="/api/docs" target="_blank" rel="noreferrer" className={btn}>
          Open docs
        </a>
        <a href="/api/v1/openapi.json" target="_blank" rel="noreferrer" className={btn}>
          OpenAPI JSON
        </a>
      </div>

      {newSecret && (
        <div className="mb-3 rounded-xl border-2 border-warn bg-warn/5 p-4">
          <div className="text-[13px] font-bold">{newSecret.label}: copy it now, it will not be shown again.</div>
          <div className="mt-2">
            <CopyText text={newSecret.value} />
          </div>
          <button className={clsx(btn, "mt-2")} onClick={() => setNewSecret(null)}>
            I've saved it
          </button>
        </div>
      )}

      <div className={clsx(card, "mb-3")}>
        <div className="mb-2 flex items-center gap-2 text-[14px] font-bold">
          <KeyRound className="size-4" /> API keys
        </div>
        <div className="divide-y divide-line">
          {d?.keys.map((k) => (
            <div key={k.id} className={clsx("flex flex-wrap items-center gap-3 py-2 text-[12.5px]", k.revokedAt && "opacity-50")}>
              <span className="w-40 font-semibold">{k.name}</span>
              <span className="font-mono text-[11.5px] text-ink-3">{k.prefix}…</span>
              <span className="flex gap-1">
                {k.scopes.map((s) => (
                  <span key={s} className="rounded bg-panel-2 px-1.5 text-[11px]">
                    {s}
                  </span>
                ))}
              </span>
              <span className="flex-1 text-[11.5px] text-ink-3">
                {k.revokedAt ? "revoked" : `last used ${k.lastUsedAt ? ago(Date.parse(k.lastUsedAt), tz) : "never"}`}
                {/* A vendor's own key says so: the vendor, not an administrator, answers for it. */}
                {k.owner ? ` · issued by vendor ${k.owner.name ?? "(account deleted)"}` : k.createdBy ? ` · issued by ${k.createdBy}` : ""}
              </span>
              {!k.revokedAt && (
                <button
                  className="text-[12px] font-semibold text-bad hover:underline"
                  onClick={async () => {
                    await api.revokeKey(k.id);
                    await refresh();
                  }}
                >
                  Revoke
                </button>
              )}
            </div>
          ))}
          {!d?.keys.length && <div className="py-2 text-[12px] text-ink-3">No keys yet.</div>}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <input className={clsx(input, "w-44")} value={keyName} onChange={(e) => setKeyName(e.target.value)} placeholder="Who uses it" />
          {(d?.scopes ?? ["read", "apron:write", "bags:write"]).map((s) => (
            <label key={s} className="flex items-center gap-1 text-[12px]" title={SCOPE_HELP[s] ?? s}>
              <input type="checkbox" checked={scopes.includes(s)} onChange={(e) => setScopes(e.target.checked ? [...scopes, s] : scopes.filter((x) => x !== s))} />
              <span>
                <b>{s}</b>
                {SCOPE_WHO[s] ? ` (${SCOPE_WHO[s]})` : ""}
              </span>
            </label>
          ))}
          <button
            className={btn}
            disabled={!keyName.trim() || scopes.length === 0}
            onClick={async () => {
              const r = await api.createKey(keyName.trim(), scopes);
              setNewSecret({ label: `API key for ${r.row.name}`, value: r.key });
              await refresh();
            }}
          >
            <Plus className="size-4" /> Create key
          </button>
        </div>
      </div>

      <div className={clsx(card, "mb-3")}>
        <div className="mb-1 flex items-center gap-2 text-[14px] font-bold">
          <Webhook className="size-4" /> Webhooks
        </div>
        <p className="mb-2 text-[12px] text-ink-3">Push allocation changes to Display the moment they happen (stand, gate, counters, belts, times, status). Each request is signed; the docs show how to check it.</p>
        <div className="divide-y divide-line">
          {d?.webhooks.map((h) => (
            <div key={h.id} className="flex flex-wrap items-center gap-3 py-2 text-[12.5px]">
              <span className="w-32 font-semibold">{h.name}</span>
              <span className="min-w-0 flex-1 truncate font-mono text-[11.5px] text-ink-2">{h.url}</span>
              <span className="text-[11.5px] text-ink-3">{h.lastStatus ? `${h.lastStatus} · ${ago(h.lastSentAt ? Date.parse(h.lastSentAt) : null, tz)}` : "not sent yet"}</span>
              <button
                className="flex items-center gap-1 text-[12px] font-semibold hover:underline"
                onClick={async () => {
                  const r = await api.testWebhook(h.id);
                  setMsg(`Test to ${h.name}: ${r.status}`);
                  await refresh();
                }}
              >
                <Send className="size-3.5" /> Test
              </button>
              <button
                className="text-[12px] font-semibold text-bad hover:underline"
                onClick={async () => {
                  await api.removeWebhook(h.id);
                  await refresh();
                }}
              >
                Remove
              </button>
            </div>
          ))}
          {!d?.webhooks.length && <div className="py-2 text-[12px] text-ink-3">No webhooks yet.</div>}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <input className={clsx(input, "w-36")} value={hookName} onChange={(e) => setHookName(e.target.value)} placeholder="Name" />
          <input className={clsx(input, "min-w-72 flex-1")} value={hookUrl} onChange={(e) => setHookUrl(e.target.value)} placeholder="https://display.example.com/hooks/yellow-better" />
          <button
            className={btn}
            disabled={!hookUrl.startsWith("https://") || !hookName.trim()}
            onClick={async () => {
              try {
                const r = await api.createWebhook(hookName.trim(), hookUrl.trim());
                setNewSecret({ label: `Signing secret for ${hookName}`, value: r.secret });
                setHookUrl("");
                await refresh();
              } catch (err) {
                setMsg(err instanceof ApiError ? err.message : "Could not add");
              }
            }}
          >
            <Plus className="size-4" /> Add webhook
          </button>
        </div>
        {msg && <div className="mt-2 text-[12px] text-ink-2">{msg}</div>}
      </div>

      <div className={card}>
        <div className="mb-1 flex items-center gap-2 text-[14px] font-bold">
          Vectro Apron
          {(() => {
            // The stored time outlives a restart; the in-memory one is a little fresher.
            const heard = d?.apron.lastReceived ?? (d?.apron.cameras?.lastHeard ? Date.parse(d.apron.cameras.lastHeard) : null);
            return <Status tone={heard ? "ok" : "off"}>{heard ? `Last event ${ago(heard, tz)}` : "No events yet"}</Status>;
          })()}
        </div>
        <p className="text-[12px] text-ink-3">
          Give Apron a key with <b>apron:write</b>. It posts to <CopyText text={`${d?.publicUrl || location.origin}/api/v1/apron/events`} /> using its v1.0 contract. Observed on/off-block times fill in actual times; an unavailable gate closes the stand. Turn status and the stills and clips of each moment (contract 1.1) show on the flight panel and as a chip on the stand board; they never change the plan.
        </p>
        {d?.apron.cameras && <CameraFeedFacts cameras={d.apron.cameras} tz={tz} />}
        {d && d.apron.alerts.length > 0 && (
          <div className="mt-2 space-y-1">
            {d.apron.alerts.map((a) => (
              <div key={a.id} className="text-[12px]">
                <span className={clsx("font-semibold", a.severity === "critical" ? "text-bad" : a.severity === "warning" ? "text-warn" : "text-ink-2")}>{a.severity}</span> · {a.category} · {a.status} ·{" "}
                <span className="text-ink-3">{stamp(a.at, tz)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

/**
 * The turn status and evidence feed (contract 1.1): when Apron was last heard, which is
 * kept in the database so a restart does not make it look silent, and what is held.
 */
function CameraFeedFacts({ cameras: c, tz }: { cameras: NonNullable<IntegrationsInfo["apron"]["cameras"]>; tz: string }) {
  const heard = c.lastHeard ? Date.parse(c.lastHeard) : null;
  const mb = (n: number) => (n >= 1024 * 1024 * 1024 ? `${(n / 1024 / 1024 / 1024).toFixed(1)} GB` : `${Math.round(n / 1024 / 1024)} MB`);
  return (
    <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-[12px] sm:grid-cols-4">
      <div>
        <div className="text-ink-3">Feed last heard</div>
        <div className="font-semibold">{heard ? ago(heard, tz) : "never"}</div>
      </div>
      <div>
        <div className="text-ink-3">Turns held</div>
        <div className="font-semibold">
          {c.turns} <span className="font-normal text-ink-3">({c.live} live)</span>
        </div>
      </div>
      <div>
        <div className="text-ink-3">Stills and clips</div>
        <div className="font-semibold">
          {c.media.count} <span className="font-normal text-ink-3">· {mb(c.media.bytes)} of {mb(c.retention.maxBytes)}</span>
        </div>
      </div>
      <div>
        <div className="text-ink-3">Kept for</div>
        <div className="font-semibold">
          {c.retention.mediaDays} days <span className="font-normal text-ink-3">media · {c.retention.turnDays} days turns</span>
        </div>
      </div>
      {!!c.unmatched?.length && (
        <div className="col-span-2 mt-1 rounded-md bg-warn/10 p-2 text-[12px] sm:col-span-4">
          <div className="font-semibold text-warn">{`${c.unmatched.length} camera turn${c.unmatched.length === 1 ? "" : "s"} today meet no flight on the board, so their pictures and clips show nowhere`}</div>
          <div className="mt-0.5 text-ink-2">Usually a flight number written differently from the airport&rsquo;s, or the wrong date. As Apron sent them:</div>
          <ul className="tabular mt-1">
            {c.unmatched.map((u) => (
              <li key={u.turnId}>{`${u.flights} · ${u.date}${u.stand ? ` · gate ${u.stand}` : ""} · ${u.media} still${u.media === 1 ? "" : "s"}/clips · ${u.turnId}`}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- usage

/**
 * Who is using the app, for a super administrator.
 *
 * Deliberately plain: how many people, when, from where, and what failed to sign in. It
 * says at the top what is recorded and for how long, because the people in this table are
 * colleagues, and an airport that watches its staff without telling them has a different
 * problem from the one this answers.
 */
export function UsageSection({ tz }: { tz: string }) {
  const [days, setDays] = useState(30);
  const q = useQuery({ queryKey: ["usage", days], queryFn: () => api.usage(days) });
  const d = q.data;
  const when = (iso: string) => stamp(iso, tz);
  const busiest = d?.hours.length ? Math.max(...d.hours.map((h) => h.events)) : 0;

  return (
    <>
      <h2 className="mb-1 text-[18px] font-bold">Usage</h2>
      <p className="mb-3 max-w-3xl text-[12.5px] text-ink-3">
        Sign-ins, changes, and a heartbeat while someone has the app open — never every request, or a board that refreshes itself would bury everything worth
        knowing. Kept for <b>{d?.keepDays ?? 90} days</b> and then deleted. Everyone can see what is recorded about themselves, and nothing is sent to any outside
        service: an address is shown as it is, named only if it matches one of the airport&apos;s own networks.
      </p>

      <div className="mb-4 flex items-center gap-2 text-[12px]">
        <span className="text-ink-3">Showing</span>
        {[7, 30, 90].map((n) => (
          <button
            key={n}
            className={clsx("rounded-lg border px-2 py-1", days === n ? "border-ink-3 bg-panel-2 font-semibold" : "border-line text-ink-2")}
            onClick={() => setDays(n)}
          >
            {n} days
          </button>
        ))}
        {q.isFetching && <Loader2 className="size-4 animate-spin text-ink-3" />}
      </div>

      {!d ? (
        <p className="text-[12.5px] text-ink-3">Loading…</p>
      ) : (
        <>
          <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className={card}>
              <div className="text-[11px] tracking-wide text-ink-3 uppercase">People</div>
              <div className="tabular text-[20px] font-bold">{d.people.length}</div>
            </div>
            <div className={card}>
              <div className="text-[11px] tracking-wide text-ink-3 uppercase">Sign-ins</div>
              <div className="tabular text-[20px] font-bold">{d.people.reduce((n, p) => n + p.signIns, 0)}</div>
            </div>
            <div className={card}>
              <div className="text-[11px] tracking-wide text-ink-3 uppercase">Changes made</div>
              <div className="tabular text-[20px] font-bold">{d.people.reduce((n, p) => n + p.changes, 0)}</div>
            </div>
            <div className={card}>
              <div className="text-[11px] tracking-wide text-ink-3 uppercase">Failed sign-ins</div>
              <div className={clsx("tabular text-[20px] font-bold", d.failures.length && "text-warn")}>{d.failures.length}</div>
            </div>
          </div>

          <div className={clsx(card, "mb-4")}>
            <div className="mb-2 text-[13px] font-semibold">When the app is used</div>
            <div className="flex h-20 items-end gap-0.5">
              {Array.from({ length: 24 }, (_, h) => {
                const n = d.hours.find((x) => x.hour === h)?.events ?? 0;
                return (
                  <div key={h} className="flex-1" title={`${String(h).padStart(2, "0")}:00 — ${n}`}>
                    <div className="w-full rounded-t bg-info" style={{ height: busiest ? `${(n / busiest) * 100}%` : 0, minHeight: n ? 2 : 0 }} />
                  </div>
                );
              })}
            </div>
            <div className="mt-1 flex justify-between text-[11px] text-ink-3">
              <span>00:00</span>
              <span>by hour of the day</span>
              <span>23:00</span>
            </div>
          </div>

          <div className="mb-1 text-[11px] font-semibold tracking-wide text-ink-3 uppercase">People</div>
          <table className="mb-4 w-full text-[12.5px]">
            <thead>
              <tr className="border-b border-line text-left text-[11px] tracking-wide text-ink-3 uppercase">
                <th className="px-2 py-1.5">Name</th>
                <th className="px-2 py-1.5">Role</th>
                <th className="px-2 py-1.5 text-right">Sign-ins</th>
                <th className="px-2 py-1.5 text-right">Changes</th>
                <th className="px-2 py-1.5">From</th>
                <th className="px-2 py-1.5 text-right">Last seen</th>
              </tr>
            </thead>
            <tbody>
              {d.people.map((p) => (
                <tr key={p.userId} className="border-b border-line">
                  <td className="px-2 py-1.5 font-semibold">{p.name}</td>
                  <td className="px-2 py-1.5 text-ink-2 capitalize">{p.role}</td>
                  <td className="tabular px-2 py-1.5 text-right">{p.signIns}</td>
                  <td className="tabular px-2 py-1.5 text-right">{p.changes}</td>
                  <td className="px-2 py-1.5 text-ink-2">{p.places.join(", ") || "—"}</td>
                  <td className="tabular px-2 py-1.5 text-right text-ink-3">{when(p.lastAt)}</td>
                </tr>
              ))}
              {!d.people.length && (
                <tr>
                  <td colSpan={6} className="px-2 py-3 text-[12px] text-ink-3">
                    Nobody has used the app in this period.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {d.failures.length > 0 && (
            <>
              <div className="mb-1 text-[11px] font-semibold tracking-wide text-ink-3 uppercase">Sign-ins that failed</div>
              <div className="mb-4 divide-y divide-line">
                {d.failures.slice(0, 12).map((f, i) => (
                  <div key={i} className="flex items-baseline gap-3 py-1.5 text-[12px]">
                    <span className="tabular w-28 shrink-0 text-ink-3">{when(f.at)}</span>
                    <span className="min-w-0 flex-1 truncate">{f.name ?? "unknown"}</span>
                    <span className="shrink-0 text-ink-3">{f.where ?? f.ip ?? ""}</span>
                  </div>
                ))}
              </div>
            </>
          )}

          <div className="mb-1 text-[11px] font-semibold tracking-wide text-ink-3 uppercase">Lately</div>
          <div className="scroll-thin max-h-72 divide-y divide-line overflow-auto">
            {d.recent.map((r, i) => (
              <div key={i} className="flex items-baseline gap-3 py-1.5 text-[12px]">
                <span className="tabular w-28 shrink-0 text-ink-3">{when(r.at)}</span>
                <span className="w-36 shrink-0 truncate font-semibold">{r.name ?? "—"}</span>
                <span className="w-24 shrink-0 text-ink-2">{r.kind}</span>
                <span className="min-w-0 flex-1 truncate text-ink-3" title={r.path ?? ""}>
                  {r.path ?? ""}
                </span>
                <span className="shrink-0 text-ink-3">{r.where ?? r.ip ?? ""}</span>
                <span className="hidden shrink-0 text-ink-3 sm:inline">{r.agent}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
}

/**
 * The people who move aircraft. A tow planned on the board is worth nothing until the
 * crew on the apron knows, so every handler on duty for that airline is texted the moment
 * a tow is planned or cancelled. No airline picked means every airline.
 */
function TowCrews({ canEdit }: { canEdit: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["handlers"], queryFn: api.handlers });
  const settings = useQuery({ queryKey: ["settings"], queryFn: api.settings });
  const airlines = settings.data ? Object.values(settings.data.config.airlines) : [];
  const [draft, setDraft] = useState<Array<{ name: string; phone: string; airlines: string[]; active: boolean }> | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const list = draft ?? q.data?.handlers.map(({ name, phone, airlines, active }) => ({ name, phone, airlines, active })) ?? [];

  return (
    <div className={clsx(card, "mb-3")}>
      <div className="mb-1 text-[14px] font-bold">Tow crews</div>
      <p className="mb-2 text-[12px] text-ink-3">
        Texted the moment a tow is planned or cancelled: the flights, the aircraft, which stand to which, and when. Pick the airlines each person handles, or none for all of them.
      </p>
      <div className="space-y-2">
        {list.map((h, i) => (
          <div key={i} className="flex flex-wrap items-center gap-2">
            <input className={clsx(input, "w-40")} value={h.name} disabled={!canEdit} placeholder="Name" onChange={(e) => setDraft(list.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} />
            <input className={clsx(input, "w-40")} value={h.phone} disabled={!canEdit} placeholder="250-555-0123" onChange={(e) => setDraft(list.map((x, j) => (j === i ? { ...x, phone: e.target.value } : x)))} />
            <div className="flex flex-wrap gap-1">
              {airlines.map((a) => {
                const on = h.airlines.includes(a.code);
                return (
                  <button
                    key={a.code}
                    type="button"
                    disabled={!canEdit}
                    title={a.name}
                    onClick={() => setDraft(list.map((x, j) => (j === i ? { ...x, airlines: on ? x.airlines.filter((z) => z !== a.code) : [...x.airlines, a.code] } : x)))}
                    className={clsx("rounded-full border px-2 py-0.5 text-[11px] font-semibold", on ? "border-transparent text-white" : "border-line text-ink-3")}
                    style={on ? { background: a.color } : undefined}
                  >
                    {a.iata}
                  </button>
                );
              })}
              {h.airlines.length === 0 && <span className="self-center text-[11px] text-ink-3">all airlines</span>}
            </div>
            <label className="flex items-center gap-1 text-[12px]">
              <input type="checkbox" checked={h.active} disabled={!canEdit} onChange={(e) => setDraft(list.map((x, j) => (j === i ? { ...x, active: e.target.checked } : x)))} /> On duty
            </label>
            {canEdit && (
              <button className="rounded p-1 text-ink-3 hover:text-bad" onClick={() => setDraft(list.filter((_, j) => j !== i))} aria-label="Remove">
                <Trash2 className="size-4" />
              </button>
            )}
          </div>
        ))}
        {list.length === 0 && <div className="text-[12px] text-ink-3">Nobody yet. Until someone is here, tows are planned on the board and nobody is texted.</div>}
      </div>
      {canEdit && (
        <div className="mt-3 flex items-center gap-2">
          <button className={btn} onClick={() => setDraft([...list, { name: "", phone: "", airlines: [], active: true }])}>
            <Plus className="size-4" /> Add person
          </button>
          {draft && (
            <button
              className={clsx(btn, "bg-ink text-panel")}
              onClick={async () => {
                setMsg(null);
                try {
                  await api.saveHandlers(draft.filter((h) => h.name.trim() && h.phone.trim()));
                  setDraft(null);
                  await qc.invalidateQueries({ queryKey: ["handlers"] });
                  setMsg("Saved.");
                } catch (err) {
                  setMsg(err instanceof Error ? err.message : "Could not save");
                }
              }}
            >
              Save
            </button>
          )}
          {msg && <span className="text-[12px] text-ink-2">{msg}</span>}
        </div>
      )}
    </div>
  );
}
